import React, { useEffect, useRef, useState } from "react";
import { IoChatbubblesOutline, IoContractOutline, IoExpandOutline, IoSend } from "react-icons/io5";

const POSITION_KEY = "xima_championship_chat_float_v1";

const readFloatState = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(POSITION_KEY) || "null");
    if (saved && typeof saved === "object") return saved;
  } catch {
    // storage unavailable: chat stays docked
  }
  return { floating: false, x: 0, y: 0 };
};

const saveFloatState = (state) => {
  try {
    window.localStorage.setItem(POSITION_KEY, JSON.stringify(state));
  } catch {
    // storage unavailable
  }
};

const clampPosition = (x, y) => ({
  x: Math.max(8, Math.min(window.innerWidth - 300, x)),
  y: Math.max(8, Math.min(window.innerHeight - 80, y)),
});

const formatTime = (at) => {
  const date = new Date(Number(at) || 0);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

// Chat del campeonato: normalmente debajo de los jugadores; se puede despegar y
// arrastrar por la pantalla (la posición se recuerda en este navegador).
const ChampionshipChat = ({ messages, canChat, onSend }) => {
  const [text, setText] = useState("");
  const [floatState, setFloatState] = useState(readFloatState);
  const listRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, floatState.floating]);

  const updateFloat = (next) => {
    setFloatState(next);
    saveFloatState(next);
  };

  const toggleFloating = () => {
    if (floatState.floating) {
      updateFloat({ ...floatState, floating: false });
      return;
    }
    const start = floatState.x || floatState.y ? clampPosition(floatState.x, floatState.y) : clampPosition(window.innerWidth - 340, window.innerHeight - 380);
    updateFloat({ floating: true, ...start });
  };

  const handlePointerDown = (event) => {
    if (!floatState.floating || event.target.closest("button")) return;
    dragRef.current = { dx: event.clientX - floatState.x, dy: event.clientY - floatState.y };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const handlePointerMove = (event) => {
    if (!dragRef.current) return;
    setFloatState((prev) => ({ ...prev, ...clampPosition(event.clientX - dragRef.current.dx, event.clientY - dragRef.current.dy) }));
  };
  const handlePointerUp = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setFloatState((prev) => {
      saveFloatState(prev);
      return prev;
    });
  };

  const submit = (event) => {
    event.preventDefault();
    if (onSend(text)) setText("");
  };

  return (
    <section
      className={`xc-chat${floatState.floating ? " is-floating" : ""}`}
      style={floatState.floating ? { left: floatState.x, top: floatState.y } : undefined}
    >
      <div
        className="xc-chat-head"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <IoChatbubblesOutline aria-hidden="true" />
        <span>CHAT DEL CAMPEONATO</span>
        <button
          type="button"
          onClick={toggleFloating}
          aria-label={floatState.floating ? "Volver a poner el chat en el panel" : "Despegar el chat y moverlo"}
          title={floatState.floating ? "Volver al panel" : "Despegar y mover"}
        >
          {floatState.floating ? <IoContractOutline /> : <IoExpandOutline />}
        </button>
      </div>
      <div ref={listRef} className="xc-chat-list" aria-live="polite">
        {messages.length ? (
          messages.map((message) => (
            <div key={message.id} className={`xc-chat-row${message.isAdmin ? " is-admin" : ""}`}>
              <strong>{message.name}</strong>
              <span>{message.text}</span>
              <small>{formatTime(message.at)}</small>
            </div>
          ))
        ) : (
          <p className="xc-chat-empty">Escribe el primer mensaje.</p>
        )}
      </div>
      {canChat ? (
        <form className="xc-chat-form" onSubmit={submit}>
          <input
            value={text}
            maxLength={300}
            placeholder="Escribe un mensaje…"
            onChange={(event) => setText(event.target.value)}
          />
          <button type="submit" aria-label="Enviar" disabled={!text.trim()}>
            <IoSend />
          </button>
        </form>
      ) : (
        <p className="xc-chat-empty">Inicia sesión con tu cuenta para escribir.</p>
      )}
    </section>
  );
};

export default ChampionshipChat;
